import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  clampRightWorkspaceSidebarWidth,
  RIGHT_WORKSPACE_SIDEBAR_DEFAULT_WIDTH_PX,
  RIGHT_WORKSPACE_SIDEBAR_MAX_WIDTH_PX,
  RIGHT_WORKSPACE_SIDEBAR_MIN_WIDTH_PX,
  RIGHT_WORKSPACE_SIDEBAR_RESIZE_STEP_PX,
} from '../features/layout/rightWorkspaceSidebarLayout';
import { storageService } from '../services/storageService';

interface DragState {
  startX: number;
  startWidth: number;
}

export function useResizableRightWorkspaceSidebarWidth(): {
  width: number;
  isResizing: boolean;
  onResizePointerDown: (event: React.PointerEvent<HTMLElement>) => void;
  onResizeKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
} {
  const [width, setWidth] = useState<number>(() => {
    const stored = storageService.getRightWorkspaceSidebarWidth();
    return clampRightWorkspaceSidebarWidth(stored ?? RIGHT_WORKSPACE_SIDEBAR_DEFAULT_WIDTH_PX);
  });
  const [isResizing, setIsResizing] = useState(false);
  const dragRef = useRef<DragState | null>(null);
  const widthRef = useRef(width);

  useEffect(() => {
    widthRef.current = width;
  }, [width]);

  const onResizePointerDown = useCallback((event: React.PointerEvent<HTMLElement>) => {
    event.preventDefault();
    const target = event.currentTarget;
    if (typeof target.setPointerCapture === 'function') {
      target.setPointerCapture(event.pointerId);
    }

    dragRef.current = { startX: event.clientX, startWidth: widthRef.current };
    setIsResizing(true);

    const previousUserSelect = document.body.style.userSelect;
    const previousCursor = document.body.style.cursor;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';

    const onMove = (moveEvent: MouseEvent) => {
      const drag = dragRef.current;
      if (!drag) return;

      const next = clampRightWorkspaceSidebarWidth(
        drag.startWidth + (drag.startX - moveEvent.clientX),
      );
      widthRef.current = next;
      setWidth(next);
    };

    const endDrag = () => {
      dragRef.current = null;
      setIsResizing(false);
      storageService.saveRightWorkspaceSidebarWidth(
        clampRightWorkspaceSidebarWidth(widthRef.current),
      );
      document.body.style.userSelect = previousUserSelect;
      document.body.style.cursor = previousCursor;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('pointerup', endDrag);
      window.removeEventListener('mouseup', endDrag);
      window.removeEventListener('pointercancel', endDrag);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('mouseup', endDrag);
    window.addEventListener('pointercancel', endDrag);
  }, []);

  const onResizeKeyDown = useCallback((event: React.KeyboardEvent<HTMLElement>) => {
    let next: number | null = null;

    if (event.key === 'ArrowLeft') {
      next = widthRef.current + RIGHT_WORKSPACE_SIDEBAR_RESIZE_STEP_PX;
    } else if (event.key === 'ArrowRight') {
      next = widthRef.current - RIGHT_WORKSPACE_SIDEBAR_RESIZE_STEP_PX;
    } else if (event.key === 'Home') {
      next = RIGHT_WORKSPACE_SIDEBAR_MIN_WIDTH_PX;
    } else if (event.key === 'End') {
      next = RIGHT_WORKSPACE_SIDEBAR_MAX_WIDTH_PX;
    }

    if (next === null) return;

    event.preventDefault();
    const clamped = clampRightWorkspaceSidebarWidth(next);
    widthRef.current = clamped;
    setWidth(clamped);
    storageService.saveRightWorkspaceSidebarWidth(clamped);
  }, []);

  return { width, isResizing, onResizePointerDown, onResizeKeyDown };
}
